import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { API_URL } from "../config";
import { UserContext } from "../context/UserContext";
import logo from "../assets/images/win11-white-logo.png";
import '../assets/css/style.css'

function Auth() {
    const navigate = useNavigate();
    const { fetchUserData } = useContext(UserContext);

    const [isLogin, setIsLogin] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // --- FORGOT PASSWORD STATES ---
    const [showForgotModal, setShowForgotModal] = useState(false);
    const [forgotStep, setForgotStep] = useState(1);
    const [forgotEmail, setForgotEmail] = useState("");
    const [otp, setOtp] = useState(new Array(6).fill(""));
    const [newPassword, setNewPassword] = useState("");
    const [forgotLoading, setForgotLoading] = useState(false);

    // 🔥 FIX: Added inputReferralCode to state
    const [form, setForm] = useState({ name: "", email: "", password: "", inputReferralCode: "" });

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        if (error) setError("");
    };

    // --- MAIN AUTH LOGIC ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        const url = isLogin ? `${API_URL}/api/auth/login` : `${API_URL}/api/auth/register`;
        const payload = { ...form, loginType: 'user' };

        try {
            const res = await axios.post(url, payload);
            if (res.data.success) {
                const token = res.data.data?.token || res.data.token;
                const userData = res.data.data?.user || res.data.user;

                localStorage.setItem("token", token);
                if (userData) {
                    localStorage.setItem("userId", userData._id || userData.id);
                    localStorage.setItem("user", JSON.stringify(userData));
                }
                await fetchUserData(token);
                navigate("/home", { replace: true });
            }
        } catch (err) {
            setError(err.response?.data?.message || "Authentication failed.");
        } finally {
            setLoading(false);
        }
    };

    // --- FORGOT PASSWORD LOGIC ---
    const handleSendOtp = async () => {
        if (!forgotEmail) return Swal.fire("Error", "Please enter your email", "error");
        setForgotLoading(true);
        try {
            const res = await axios.post(`${API_URL}/api/auth/forgot-password`, { email: forgotEmail });
            if (res.data.success) {
                setForgotStep(2);
            }
        } catch (err) {
            Swal.fire("Oops!", err.response?.data?.message || "Failed to send OTP", "error");
        } finally {
            setForgotLoading(false);
        }
    };

    const handleVerifyAndReset = async () => {
        const finalOtp = otp.join("");
        if (finalOtp.length < 6 || !newPassword) return Swal.fire("Error", "Please fill all details", "warning");

        setForgotLoading(true);
        try {
            const res = await axios.post(`${API_URL}/api/auth/reset-password`, {
                email: forgotEmail,
                otp: finalOtp,
                newPassword
            });
            if (res.data.success) {
                // ✅ SUCCESS SWEET ALERT & REFRESH
                Swal.fire({
                    title: "Success!",
                    text: "Password Reset Successfully. Redirecting to Login...",
                    icon: "success",
                    confirmButtonColor: "#800000",
                }).then(() => {
                    window.location.reload(); // Page refresh karke login par bhej dega
                });
            }
        } catch (err) {
            Swal.fire("Error", "Invalid OTP or request failed", "error");
        } finally {
            setForgotLoading(false);
        }
    };

    const handleOtpChange = (element, index) => {
        if (isNaN(element.value)) return false;
        setOtp([...otp.map((d, idx) => (idx === index ? element.value : d))]);
        if (element.value && element.nextSibling) {
            element.nextSibling.focus();
        }
    };

    return (
        <div className="auth-container">

            {/* --- TOP BRANDING SECTION --- */}
            <div className="auth-header">
                <div className="logo-wrapper">
                    <img src={logo} alt="Win11 Logo" className="brand-logo " />
                </div>
                <div className="header-tagline fw-bold">Enter the World of Elite Fantasy</div>
            </div>

            <div className="container px-4 main-content">
                <div className="auth-card shadow-lg animate-up">
                    <div className="card-body p-4 p-md-5">

                        <div className="text-center mb-4">
                            <h3 className="maroon-text">
                                {isLogin ? "Welcome Back" : "Create Account"}
                            </h3>
                            <p className="text-muted  fw-bold small">
                                {isLogin ? "Login to your account" : "Sign up to get started"}
                            </p>
                        </div>

                        {error && (
                            <div className="custom-error-alert animate-shake">
                                <i className="bi bi-exclamation-octagon-fill me-2"></i>{error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            {!isLogin && (
                                <div className="custom-input-group mb-3">
                                    <i className="bi bi-person input-icon"></i>
                                    <input type="text" className="custom-input" placeholder="Full Name" name="name" value={form.name} onChange={handleChange} required />
                                </div>
                            )}

                            <div className="custom-input-group mb-3">
                                <i className="bi bi-envelope input-icon"></i>
                                <input type="email" className="custom-input" placeholder="Email Address" name="email" value={form.email} onChange={handleChange} required />
                            </div>

                            <div className="custom-input-group mb-3">
                                <i className="bi bi-lock input-icon"></i>
                                <input type={showPassword ? "text" : "password"} className="custom-input" placeholder="Password" name="password" value={form.password} onChange={handleChange} required />
                                <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"} pass-toggle`} onClick={() => setShowPassword(!showPassword)}></i>
                            </div>

                            {/* 🔥 NEW: Referral Code Field (Only for Signup) */}
                            {!isLogin && (
                                <div className="custom-input-group mb-3">
                                    <i className="bi bi-gift input-icon"></i>
                                    <input 
                                        type="text" 
                                        className="custom-input text-uppercase" 
                                        placeholder="Invite Code (Optional)" 
                                        name="inputReferralCode" 
                                        value={form.inputReferralCode} 
                                        onChange={handleChange} 
                                    />
                                </div>
                            )}

                            {isLogin && (
                                <div className="text-end mb-4">
                                    <span className="forgot-link" onClick={() => setShowForgotModal(true)}>Forgot Password?</span>
                                </div>
                            )}

                            <button type="submit" className="btn-maroon w-100 mt-2" disabled={loading}>
                                {loading ? <span className="spinner-border spinner-border-sm"></span> : (isLogin ? "SIGN IN" : "CREATE ACCOUNT")}
                            </button>
                        </form>

                        <div className="switch-text mt-4">
                            <span className="fw-bold">{isLogin ? "New to Win11?" : "Already a member?"}</span>
                            <button className="switch-btn" onClick={() => { setIsLogin(!isLogin); setError(""); }}>
                                {isLogin ? "Register Now" : "Login Instead"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- FORGOT PASSWORD MODAL (MAROON THEME) --- */}
            {showForgotModal && (
                <div className="modal-overlay">
                    <div className="custom-modal animate-up">
                        <div className="modal-header-maroon">
                            <h5 className="mb-0 fw-bold text-white">Reset Password</h5>
                            <button className="close-btn" onClick={() => setShowForgotModal(false)}><i className="bi bi-x-lg"></i></button>
                        </div>

                        <div className="p-4">
                            {forgotStep === 1 ? (
                                <div className="text-center">
                                    <div className="modal-icon-box"><i className="bi bi-send-check"></i></div>
                                    <p className="text-muted mb-4">We will send a 6-digit OTP to your registered email.</p>
                                    <div className="custom-input-group mb-4">
                                        <i className="bi bi-envelope input-icon"></i>
                                        <input type="email" className="custom-input" placeholder="Enter Registered Email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} />
                                    </div>
                                    <button className="btn-maroon w-100 py-3" onClick={handleSendOtp} disabled={forgotLoading}>
                                        {forgotLoading ? <span className="spinner-border spinner-border-sm"></span> : "SEND OTP"}
                                    </button>
                                </div>
                            ) : (
                                <div className="text-center">
                                    <p className="text-muted mb-3">OTP sent to <b>{forgotEmail}</b></p>
                                    <div className="d-flex justify-content-center gap-2 mb-4">
                                        {otp.map((data, index) => (
                                            <input key={index} type="text" maxLength="1" className="otp-box"
                                                value={data} onChange={(e) => handleOtpChange(e.target, index)}
                                                onFocus={(e) => e.target.select()} />
                                        ))}
                                    </div>
                                    <div className="custom-input-group mb-4">
                                        <i className="bi bi-shield-lock input-icon"></i>
                                        <input type="password" className="custom-input" placeholder="Set New Password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                                    </div>
                                    <button className="btn-maroon w-100 py-3" onClick={handleVerifyAndReset} disabled={forgotLoading}>
                                        {forgotLoading ? <span className="spinner-border spinner-border-sm"></span> : "UPDATE PASSWORD"}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

export default Auth;